import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {createRefetchContainer, graphql} from 'react-relay';
import scrollToComponent from 'react-scroll-to-component';
import {Redirect} from 'react-router-dom';

import EventOnlineView from '../components/content/event/EventOnlineView';
import Faq from '../components/content/Faq';
import InvalidFaceFileInformation from '../components/content/informations/InvalidFaceFileInformation';
import InvalidStartnumberInformation from '../components/content/informations/InvalidStartnumberInformation';
import LoadingView from '../components/content/LoadingView';
import DualSearchMediaList from '../components/content/lists/DualSearchMediaList';
import withRequestHandler from '../components/hoc/withRequestHandler';
import SecondFactorErrorMsg from '../components/messages/SecondFactorErrorMsg';

class EventDualSearchPage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      extendedSearchSelectedTab: 3,
      extendedSearchExpand: true,
      showNoPhotosFoundInformation: false,
    };
  }

  componentDidMount() {
    this._scrollToMediaSection();
  }

  componentDidUpdate(prevProps) {
    const {searchStartnumberFromUrl} = this.props;
    if (searchStartnumberFromUrl !== prevProps.searchStartnumberFromUrl) {
      this._refetchMediaForNewSearch(searchStartnumberFromUrl);
      this._scrollToMediaSection();
    }
  }

  handleExtendedSearchTabChanged = (event, value) => {
    this.setState({
      extendedSearchSelectedTab: value,
      extendedSearchExpand: true,
    });
  };

  handleExtendedSearchExpand = () => {
    this.setState(prevState => {
      const {
        extendedSearchExpand: prevExpand,
        extendedSearchSelectedTab: prevSelectedTab,
      } = prevState;

      return {
        extendedSearchExpand: !prevExpand,
        // if the extended search changes from open to close it is important to reset
        // the selected tab to its default. Otherwise the media list will not be rendered.
        extendedSearchSelectedTab: prevExpand ? 3 : prevSelectedTab,
      };
    });
  };

  handleDeepSearchBtnClicked = () => {
    this.scrollToEventOnlineView(0);
  };

  handleCategorySearchBtnClicked = () => {
    this.scrollToEventOnlineView(1);
  };

  handleGpsSearchBtnClicked = () => {
    this.scrollToEventOnlineView(2);
  };

  handleDualSearchBtnClicked = () => {
    this.scrollToEventOnlineView(3);
  };

  scrollToEventOnlineView = (tabIndex) => {
    scrollToComponent(this.eventOnlineView);
    this.setState({
      extendedSearchExpand: true,
      extendedSearchSelectedTab: tabIndex,
    });
  }

  _refetchMediaForNewSearch = newSearchStartnumber => {
    this.props.onRequestStart();
    this.setState({showNoPhotosFoundInformation: false});
    this.props.relay.refetch({searchStartnumber: newSearchStartnumber}, null, err => {
      if (err) {
        this.props.onRequestStop();
        this.setState({showNoPhotosFoundInformation: true});
      } else {
        this.props.onRequestStop();
      }
    });
  };

  _scrollToMediaSection = () => {
    if (this.mediaSection) {
      scrollToComponent(this.mediaSection, {align: 'top'});
    }
  }


  render() {
    const {
      eventHasSecondFactor,
      isLoading,
      snFromUrl,
      searchStartnumberFromUrl,
      viewer,
      customerKey,
    } = this.props;
    const {event} = viewer;

    if (event.state === 'reminder') {
      return <Redirect to={`/event/${event.sgId}`} />;
    }

    const participantMediaCount = event.participant
      ? event.participant.media.mediaInfo.count
      : 0;

    const {
      extendedSearchSelectedTab,
      extendedSearchExpand,
      showNoPhotosFoundInformation,
    } = this.state;

    return (
      <div className="container-960 container-page">
        <EventOnlineView
          ref={view => (this.eventOnlineView = view)}
          onlineEvent={event}
          urlSn={snFromUrl}
          urlSearchSn={searchStartnumberFromUrl}
          customerKey={customerKey}
          extendedSearchSelectedTab={extendedSearchSelectedTab}
          extendedSearchExpand={extendedSearchExpand}
          hasSmartSearchTab={event.smartSearch && participantMediaCount !== 0}
          hasGpsSearchTab={event.gps}
          hasDualSearchTab={event.searchDual}
          onExtendedSearchTabChange={this.handleExtendedSearchTabChanged}
          onExtendedSearchExpand={this.handleExtendedSearchExpand}
        />
        <div
          ref={div => {
            this.mediaSection = div;
          }}
        >
          {isLoading && <LoadingView />}
          {!isLoading && event.participant === null && (
            <SecondFactorErrorMsg event={event}/>
          )}
          {!isLoading && event.participant !== null && extendedSearchSelectedTab === 3 && (
            <>
              {!showNoPhotosFoundInformation &&
                event.participant.suggestionsByStartnumber.mediaInfo.count !== 0 && (
                  <DualSearchMediaList
                    eventHasSearchByFace={event.searchByFace ? true : false}
                    eventHasSecondFactor={eventHasSecondFactor}
                    eventId={event.sgId}
                    startnumber={snFromUrl}
                    searchStartnumber={searchStartnumberFromUrl}
                    media={event.participant.suggestionsByStartnumber}
                    offer={event}
                    participantMediaCount={participantMediaCount}
                  />
                )}
              {(
                showNoPhotosFoundInformation ||
                event.participant.suggestionsByStartnumber.mediaInfo.count === 0
              ) && (
                  event.searchByFace &&
                  <InvalidStartnumberInformation /> ||
                  <InvalidFaceFileInformation />
                )}
            </>
          )}
        </div>
        <Faq onFindMore={() => this.scrollToEventOnlineView(0)} />
      </div>
    );
  }
}

EventDualSearchPage.propTypes = {
  customerKey: PropTypes.string,
  eventHasSearchByFace: PropTypes.bool,
  eventHasSecondFactor: PropTypes.bool,
  isLoading: PropTypes.bool,
  snFromUrl: PropTypes.string,
  searchStartnumberFromUrl: PropTypes.string,
  viewer: PropTypes.object,
  relay: PropTypes.object,
  onRequestStart: PropTypes.func,
  onRequestStop: PropTypes.func,
};

export {EventDualSearchPage};

export default createRefetchContainer(
  withRequestHandler(EventDualSearchPage),
  {
    viewer: graphql`
      fragment EventDualSearchPage_viewer on Viewer {
        event(id: $eventId, language: $language) {
          id
          sgId
          gps
          hostResultUrl

          participant(startnumber: $startnumber, secondFactor: $secondFactor) {
            media {
              mediaInfo {
                count
              }
            }

            suggestionsByStartnumber(startnumber: $searchStartnumber) {
              mediaInfo {
                count
              }
              ...DualSearchMediaList_media
            }

            startnumber
          }
          secondFactorType {
            type
          }
          searchByFace
          searchDual
          smartSearch
          state
          ...EventOnlineView_onlineEvent
          ...DualSearchMediaList_offer
        }
      }
    `,
  },
  graphql`
    query EventDualSearchPageRefetchQuery(
      $eventId: ID!
      $language: String
      $startnumber: ID!
      $searchStartnumber: ID!
      $count: Int!
      $cursor: String
      $secondFactor: String
      $individualPart: String
    ) {
      viewer {
        ...EventDualSearchPage_viewer
      }
    }
  `
);
